import { useBreakpointDown, useIsInResponsivePageActions } from '@chronosphereio/chrono-ui';
import { isTimeRangeInputValueAbsolute, TimeRangeInputValue } from '@chronosphereio/core';

export type ResponsiveTimeRangeControlsVariant = 'default' | 'compact';

export interface UseResponsiveTimeRangeControlsResult {
  /**
   * Returns `true` if the time range controls are inside a `ResponsivePageActions` component. Responsive behavior
   * cannot be enabled in the old page actions.
   */
  responsiveEnabled: boolean;

  /**
   * Variant of the time range controls to render. Currently supports two variants:
   * - `default`: rendering for larger screens and use cases that have not migrated to the new responsive
   *   page actions.
   * - `compact`: compact variant used for smaller screens (below the `sm` breakpoint) for use cases that have migrated
   *   to the new responsive page actions.
   */
  responsiveVariant: ResponsiveTimeRangeControlsVariant;
}

/**
 * Hook that helps manage responsive behavior for the time range controls when they are used inside the new
 * `ResponsivePageActions`. This assists with slowly migrating to the new responsive page actions incrementally without
 * breaking old use cases.
 */
export const useResponsiveTimeRangeControls = (): UseResponsiveTimeRangeControlsResult => {
  const isInResponsivePageActions = useIsInResponsivePageActions();
  const isBelowSmBreakpoint = useBreakpointDown('sm', { breakpointSource: 'element' });

  const responsiveEnabled = isInResponsivePageActions;

  // We do not support the "compact" variant in the old page actions because the CSS will not work properly with them.
  // Teams should migrate to the new responsive page actions to get the responsive time range controls behavior.
  const responsiveVariant: ResponsiveTimeRangeControlsVariant =
    responsiveEnabled && isBelowSmBreakpoint ? 'compact' : 'default';

  return {
    responsiveEnabled,
    responsiveVariant,
  };
};

const RESPONSIVE_TIME_RANGE_SELECT_MIN_WIDTH = {
  absolute: 300,
  relative: 200,
};

/**
 * Returns recommended minimum widths for the time range controls when rendered responsively. The value varies based
 * on the type of time range (relative vs. absolute) because they need different minimum amounts of space to be
 * legible.
 */
export function getResponsiveTimeRangeSelectMinWidth(timeRange: TimeRangeInputValue) {
  const isAbsolute = isTimeRangeInputValueAbsolute(timeRange);

  if (isAbsolute) {
    return RESPONSIVE_TIME_RANGE_SELECT_MIN_WIDTH.absolute;
  }

  return RESPONSIVE_TIME_RANGE_SELECT_MIN_WIDTH.relative;
}
